/**
 * Shared UI component for UI Stock Selector.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';
import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiStockProductOption, UiStockSelectorValue, UiStockVariantOption } from './ui-stock-selector.types';

/**
 * COMPONENT: Ui Stock Selector
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-stock-selector',
  standalone: true,
  imports: [UiSelectComponent, UiFormFieldComponent],
  templateUrl: './ui-stock-selector.component.html',
  styleUrl: './ui-stock-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiStockSelectorComponent {
  private readonly defaultCategories: UiSelectOption[] = [
    { label: 'shared.stockSelector.defaultCategories.sim', value: 'SIM' },
    { label: 'shared.stockSelector.defaultCategories.float', value: 'Float' },
    { label: 'shared.stockSelector.defaultCategories.cash', value: 'Cash' },
    { label: 'shared.stockSelector.defaultCategories.device', value: 'Device' }
  ];

  readonly value = input<UiStockSelectorValue>({ categoryId: '', productId: '', variantId: '', variantIds: [], quantity: null });
  readonly categories = input<UiSelectOption[]>([]);
  readonly products = input<UiStockProductOption[]>([]);
  readonly variants = input<UiStockVariantOption[]>([]);
  /** PARENT-CHILD CONTRACT: categoryLabel documents the input state boundary used by this file. */
  readonly categoryLabel = input('shared.stockSelector.labels.category');
  /** PARENT-CHILD CONTRACT: productLabel documents the input state boundary used by this file. */
  readonly productLabel = input('shared.stockSelector.labels.product');
  /** PARENT-CHILD CONTRACT: variantLabel documents the input state boundary used by this file. */
  readonly variantLabel = input('shared.stockSelector.labels.variant');
  /** PARENT-CHILD CONTRACT: quantityLabel documents the input state boundary used by this file. */
  readonly quantityLabel = input('shared.stockSelector.labels.quantity');
  /** PARENT-CHILD CONTRACT: categoryPlaceholder keeps the default stock-selector prompt localized. */
  readonly categoryPlaceholder = input('shared.stockSelector.placeholders.category');
  /** PARENT-CHILD CONTRACT: productPlaceholder keeps the default stock-selector prompt localized. */
  readonly productPlaceholder = input('shared.stockSelector.placeholders.product');
  /** PARENT-CHILD CONTRACT: variantPlaceholder keeps the default stock-selector prompt localized. */
  readonly variantPlaceholder = input('shared.stockSelector.placeholders.variant');
  /** PARENT-CHILD CONTRACT: quantityError documents the input state boundary used by this file. */
  readonly quantityError = input('');
  /** PARENT-CHILD CONTRACT: showVariant documents the input state boundary used by this file. */
  readonly showVariant = input(true);
  /** PARENT-CHILD CONTRACT: allowMultipleVariants documents the input state boundary used by this file. */
  readonly allowMultipleVariants = input(false);
  /** PARENT-CHILD CONTRACT: showQuantity documents the input state boundary used by this file. */
  readonly showQuantity = input(true);
  readonly valueChange = output<UiStockSelectorValue>();

  readonly categoryOptions = computed<UiSelectOption[]>(() => {
    const configuredCategories = this.normalizeOptions(this.categories());

    if (configuredCategories.length > 0) {
      return configuredCategories;
    }

    const productCategories = this.normalizeOptions(
      this.products()
        .map((product) => product.categoryId)
        .filter(Boolean)
        .map((categoryId) => ({ label: categoryId, value: categoryId }))
    );

    return productCategories.length > 0 ? productCategories : this.defaultCategories;
  });

  /** STATE OWNER: filteredProducts documents the computed state boundary used by this file. */
  readonly filteredProducts = computed(() => {
    const categoryId = this.value().categoryId;
    const products = this.products()
      .filter((product) => !categoryId || product.categoryId === categoryId)
      .map((product) => ({ label: product.label, value: product.value }));

    return this.normalizeOptions(products);
  });

  /** STATE OWNER: filteredVariants documents the computed state boundary used by this file. */
  readonly filteredVariants = computed(() => {
    const productId = this.value().productId;
    const variants = this.variants()
      .filter((variant) => !productId || variant.productId === productId)
      .map((variant) => ({ label: variant.label, value: variant.value }));

    return this.normalizeOptions(variants);
  });

  /**
   * BUSINESS LOGIC: selectCategory
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectCategory(categoryId: string): void {
    this.emit({ categoryId, productId: '', variantId: '', variantIds: [] });
  }

  /**
   * BUSINESS LOGIC: selectProduct
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectProduct(productId: string): void {
    this.emit({ productId, variantId: '', variantIds: [] });
  }

  /**
   * BUSINESS LOGIC: selectVariant
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectVariant(variantId: string): void {
    this.emit({ variantId, variantIds: variantId ? [variantId] : [] });
  }

  /**
   * BUSINESS LOGIC: selectVariants
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectVariants(variantIds: string[]): void {
    this.emit({ variantId: variantIds[0] ?? '', variantIds });
  }

  /**
   * BUSINESS LOGIC: setQuantity
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setQuantity(value: string): void {
    const parsed = Number(value);
    this.emit({ quantity: Number.isFinite(parsed) ? parsed : null });
  }

  /**
   * BUSINESS LOGIC: emit
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private emit(patch: Partial<UiStockSelectorValue>): void {
    this.valueChange.emit({ ...this.value(), ...patch });
  }

  /**
   * BUSINESS LOGIC: normalizeOptions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeOptions(options: UiSelectOption[]): UiSelectOption[] {
    const seen = new Set<string>();

    return options
      .map((option) => ({
        ...option,
        label: String(option.label ?? '').trim(),
        value: String(option.value ?? '').trim()
      }))
      .filter((option) => {
        if (!option.label || !option.value || seen.has(option.value)) {
          return false;
        }

        seen.add(option.value);
        return true;
      });
  }
}
